import { Ban, CheckCircle2, TriangleAlert, UserMinus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { announceAttritionCategoriesChanged } from '../../../../lib/navEvents';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { attritionCategoriesApi } from '../attritionCategoriesApi';
import type { AttritionCategoryDetail } from '../attritionCategoryTypes';
import { AttritionCategoryModal } from '../components/AttritionCategoryModal';

type TabKey = 'info' | 'checklist' | 'clearances';

/** What is open: one section's own edit, or a dialog that only confirms. One at a time. */
type Pending = 'details' | 'checklist' | 'clearances' | 'delete' | 'restore' | null;

/**
 * One attrition category, on the header layout, with THREE tabs.
 *
 * **Every section has its own edit, and each writes only its own section** (product call,
 * 30 Oct 2026). Info carries Details — name and description, its own Edit — and the Audit, which
 * has none. The document checklist and the org-unit clearances (03 Nov 2026) are each their OWN
 * TAB with their own Edit and their own write (`PUT …/documents`, `PUT …/clearances`); no form
 * can reach another's fields.
 */
export function AttritionCategoryProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [category, setCategory] = useState<AttritionCategoryDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      attritionCategoriesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setCategory(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this category.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Attrition',
    { label: 'Settings', to: '/attrition/settings' },
    { label: 'Categories', to: '/attrition/settings/categories' },
    category?.name ?? 'Category',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={3} />
      </div>
    );
  }

  const record = category;
  const stranded = record.documents.filter((d) => !d.reviewerIsActive);
  const strandedClearances = record.clearances.filter((c) => !c.signatoryIsActive);

  async function runConfirmed() {
    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await attritionCategoriesApi.deactivate(record.id);
      else await attritionCategoriesApi.activate(record.id);

      announceAttritionCategoriesChanged();

      toast.success(`${record.name} was ${pending === 'delete' ? 'deleted' : 'restored'}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="attrition-category-profile"
        header={
          <ProfileHeader
            id="attrition-category-profile-header"
            icon={<UserMinus className="h-6 w-6" aria-hidden="true" />}
            title={record.name}
            meta={[
              `${record.documents.length} ${record.documents.length === 1 ? 'document' : 'documents'}`,
              `${record.requestCount} ${record.requestCount === 1 ? 'request' : 'requests'}`,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: each section's card owns its own.
            settings={{
              onHistory: () => setHistoryOpen(true),
              menuItems: record.isActive
                ? []
                : [
                    {
                      key: 'restore',
                      label: 'Restore',
                      permission: 'attrition-categories.delete',
                      onClick: () => setPending('restore'),
                    },
                  ],
              onDelete: () => setPending('delete'),
              deletePermission: 'attrition-categories.delete',
              deleteDisabledReason: record.isActive ? null : 'This category is already deleted.',
            }}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This page may be out of date: ${error.message}`}
            />
          ) : !record.isActive ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This category is deleted. It is not offered when a request is filed; the requests already filed under it keep it."
            />
          ) : record.documents.length === 0 ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message="This category has no documents on its checklist, so no request can be filed under it yet."
            />
          ) : stranded.length > 0 ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message={`${stranded.length} ${stranded.length === 1 ? 'document is' : 'documents are'} reviewed by a deactivated account, so nobody can review ${stranded.length === 1 ? 'it' : 'them'}.`}
            />
          ) : strandedClearances.length > 0 ? (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message={`${strandedClearances.length} ${strandedClearances.length === 1 ? 'clearance is' : 'clearances are'} signed by a deactivated account, so no request filed under this category can be completed.`}
            />
          ) : (
            <ProfileBanner
              id="attrition-category-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                record.clearances.length === 0
                  ? 'A request filed under this category asks for every document on its checklist.'
                  : `A request filed under this category asks for every document on its checklist, and must be cleared by ${record.clearances.length} ${record.clearances.length === 1 ? 'unit' : 'units'}.`
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'checklist',
            label: (
              <>
                Checklist <CountBadge count={record.documents.length} />
              </>
            ),
          },
          {
            key: 'clearances',
            label: (
              <>
                Clearances <CountBadge count={record.clearances.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'clearances' ? (
          <ProfileCard
            id="attrition-category-profile-clearances"
            title="Clearances"
            count={record.clearances.length}
            onEdit={() => setPending('clearances')}
            editPermission="attrition-categories.update"
            // None is a valid answer — said so, with the card's own Edit as the way to add one.
            empty={
              record.clearances.length === 0
                ? {
                    title: 'No unit has to clear the employee',
                    description: 'A request filed under this category completes once every document is approved.',
                  }
                : undefined
            }
          >
            {record.clearances.length === 0 ? null : (
              <PreviewTable
                variant="table-display"
                rows={record.clearances.map((clearance) => ({
                  label: clearance.orgUnitName,
                  value: (
                    <span>
                      Signed by {clearance.signatoryName ?? 'an account no longer listed'}
                      {clearance.signatoryIsActive ? null : (
                        <span className="ml-2 text-xs text-red-700">Account deactivated</span>
                      )}
                      {clearance.orgUnitIsActive ? null : (
                        <span className="ml-2 text-xs text-slate-400">Unit deleted from the chart</span>
                      )}
                    </span>
                  ),
                }))}
              />
            )}
          </ProfileCard>
        ) : tab === 'checklist' ? (
          <ProfileCard
            id="attrition-category-profile-checklist"
            title="Document checklist"
            count={record.documents.length}
            onEdit={() => setPending('checklist')}
            editPermission="attrition-categories.update"
            // An empty checklist says so AND offers the way out — the card's own Edit.
            empty={
              record.documents.length === 0
                ? { title: 'No documents on the checklist yet', description: 'A request can be filed under this category once it asks for at least one document.' }
                : undefined
            }
          >
            {record.documents.length === 0 ? null : (
              <PreviewTable
                variant="table-display"
                rows={record.documents.map((document) => ({
                  label: document.name,
                  value: (
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span>
                        Reviewed by {document.reviewerName ?? 'an account no longer listed'}
                        {document.reviewerIsActive ? null : (
                          <span className="ml-2 text-xs text-red-700">Account deactivated</span>
                        )}
                      </span>
                      {document.description ? (
                        <span className="text-xs text-slate-400">{document.description}</span>
                      ) : null}
                    </span>
                  ),
                }))}
              />
            )}
          </ProfileCard>
        ) : (
          <>
            <ProfileCard
              id="attrition-category-profile-details"
              title="Details"
              onEdit={() => setPending('details')}
              editPermission="attrition-categories.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Name', value: record.name },
                  { label: 'Description', value: record.description },
                  { label: 'Order', value: String(record.sortOrder), mono: true },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="attrition-category-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending === 'details' || pending === 'checklist' || pending === 'clearances' ? (
        <AttritionCategoryModal
          open
          section={pending}
          editing={record}
          onClose={() => setPending(null)}
          onSaved={(saved) => {
            setCategory(saved);
            setPending(null);
          }}
        />
      ) : null}

      {pending === 'delete' || pending === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending === 'delete' ? 'Delete category' : 'Restore category'}
          danger={pending === 'delete'}
          requireConfirm={pending === 'delete'}
          requireCredential={pending === 'delete'}
          loading={running}
          id={`attrition-category-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'delete'
                  ? `"${record.name}" stops being offered when a request is filed. The ${record.requestCount} filed under it keep it.`
                  : `"${record.name}" is offered again when a request is filed.`}
              </p>
              <p>Nothing is erased — a deleted category can be restored.</p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="attrition-categories"
        entityId={record.id}
      />
    </div>
  );
}
